/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.18.0
 * @since 1.18.0
 */

import { Fragment, useCallback, useEffect } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import ButtonServiceUserDenied from '@/main/modules/role/components/ButtonServiceUserDenied';

import { LinkBlock, Table } from '../../../../components/common';
import PageTitle, {
    renderDropFilterButton,
    renderSettingButton,
} from '../../../../components/common/PageTitle/PageTitle';
import { tableRowRestoreAllAction } from '../../../../components/Table/actions/tableActions';
import { useTable } from '../../../../components/Table/hooks/useTable';
import { ITableField } from '../../../../components/Table/reducers/tableReducer';
import TableColumnUtil from '../../../../components/Table/TableColumnUtil';
import {
    MapTableNameToApiPath,
    MapTableNameToBasketApiPath,
    MapTableNameToLabelsApiPath,
    MapTableNameToListApiPath,
} from '../../../../components/Table/types/tableMapTypes';
import { ITableTableNamePayload, TableNameType } from '../../../../components/Table/types/tableTypes';
import { API_AUTO_REPORT_ID_PARAM } from '../../../../modules/app/Api';
import { appHistoryPush } from '../../../../modules/app/appHistory';
import DensityButton from '../../../../modules/density/components/DensityButton';
import {
    makeSelectPageAutoReportIdTranslation,
    makeSelectTranslationCommon,
    makeSelectTranslationPagePersonalAccount,
    makeSelectTranslationPageSubscriberMobile,
} from '../../../../modules/internationalization/intSelectors';
import {
    ICommonTranslation,
    IPageAutoReportIdTranslation,
    IPagePersonalAccountTranslation,
    IPageSubscriberMobileTranslation,
} from '../../../../modules/internationalization/types';
import { ECommonTranslationPages } from '../../../../modules/internationalization/types/common/pages';
import { makeSelectIsDensity } from '../../../../modules/storage/storageSelectors';
import { IRootReducer } from '../../../../reducers/rootReducer';
import { openRoute } from '../../../../routes/constants/externalRoutes';
import { stringFormatParams } from '../../../../utils/stringFormat';
import { autoReportIdSaveTableAction } from '../../actions';
import { IAutoReportIdSaveTablePayload } from '../../types';
import { getPageAutoReportIdEditTableUrl, getPageAutoReportIdTableUrl } from '../../utils/getPageAutoReportIdUrl';
import { AutoReportIdTableFooterStyled } from '../styled/AutoReportIdTableStyles';
import {
    EAutoReportIdTableAccountField,
    mapAutoReportIdTableApiPath,
    mapAutoReportIdTableBasketApiPath,
    mapAutoReportIdTableNameToColumnUtil,
    TTableTranslation,
} from './AutoReportIdTableCell';

interface IAutoReportIdTable {
    id: string;
    tableName: TableNameType.autoReportIdAccount
        | TableNameType.autoReportIdSubscriber
        | TableNameType.autoReportIdAccountEdit
        | TableNameType.autoReportIdSubscriberEdit;
}

const AutoReportIdTable = ({
                               id,
                               isDensity,
                               tableName,
                               translationAutoReportId,
                               translationCommon,
                               translationPagePersonalAccount,
                               translationSubscriberMobile,
                               restoreAllRows,
                               saveTable,
                           }: IAutoReportIdTable & IConnectedProps & IDispatchProps) => {

    const {
        basketAllIn,
        componentsFeature,
        count,
        data,
        fields,
        isLoading,
        removedRowIndexes,
        sort,
        tableCheckedItems,

        dropFilterAndSort,
        isFilterSelected,
        loadTableList,
        loadTableMeta,
        setTableSort,
        switchCheckbox,
    } = useTable(tableName);

    const isEditTable = tableName === TableNameType.autoReportIdAccountEdit
        || tableName === TableNameType.autoReportIdSubscriberEdit;

    useEffect(() => {
        if (id) {
            const tableApiUrl = stringFormatParams(mapAutoReportIdTableApiPath[tableName], {
                [API_AUTO_REPORT_ID_PARAM]: id,
            });
            const tableBasketApiUrl = stringFormatParams(mapAutoReportIdTableBasketApiPath[tableName], {
                [API_AUTO_REPORT_ID_PARAM]: id,
            });

            MapTableNameToApiPath[tableName] = tableApiUrl;
            MapTableNameToLabelsApiPath[tableName] = tableApiUrl;
            MapTableNameToListApiPath[tableName] = tableApiUrl;
            MapTableNameToBasketApiPath[tableName] = tableBasketApiUrl;

            loadTableMeta();
        }
    }, [id, tableName, loadTableMeta]);


    const clearFilterHandler = useCallback(() => dropFilterAndSort({}), [dropFilterAndSort]);

    const editTableHandler = useCallback(() => {
        appHistoryPush(getPageAutoReportIdEditTableUrl(id, tableName));
    }, [id, tableName]);

    /*
    * -------------------------------------------
    *           Table Footer
    * -------------------------------------------
    */

    const isTableBtnDisabled = isEditTable ? false : !removedRowIndexes?.length;

    const tableSaveHandler = useCallback(() => {
        saveTable({ tableName });
    }, [saveTable, tableName]);

    const tableCancelHandler = useCallback(() => {
        switch (tableName) {
            case TableNameType.autoReportIdAccountEdit:
            case TableNameType.autoReportIdSubscriberEdit:
                openRoute(getPageAutoReportIdTableUrl(id, tableName));
                break;
            case TableNameType.autoReportIdAccount:
            case TableNameType.autoReportIdSubscriber:
                restoreAllRows({ tableName });
                break;
        }
    }, [id, tableName, restoreAllRows]);

    /*
    * -------------------------------------------
    *         Table Field Configuration
    * -------------------------------------------
    */

    const rowDataName = ({
        [TableNameType.autoReportIdAccount]: '',
        [TableNameType.autoReportIdSubscriber]: '',
        [TableNameType.autoReportIdAccountEdit]: 'number',
        [TableNameType.autoReportIdSubscriberEdit]: 'msisdn',
    }[tableName]);

    const tableTitleTranslation = ({
        [TableNameType.autoReportIdAccount]: translationAutoReportId.accounts.title,
        [TableNameType.autoReportIdSubscriber]: translationAutoReportId.subscribers.title,
        [TableNameType.autoReportIdAccountEdit]: translationAutoReportId.accountsEdit.title,
        [TableNameType.autoReportIdSubscriberEdit]: translationAutoReportId.subscribersEdit.title,
    }[tableName]);

    const tableFieldsTranslation = ({
        [TableNameType.autoReportIdAccount]: translationAutoReportId.accounts,
        [TableNameType.autoReportIdSubscriber]: translationAutoReportId.subscribers,
        [TableNameType.autoReportIdAccountEdit]: translationPagePersonalAccount,
        [TableNameType.autoReportIdSubscriberEdit]: translationSubscriberMobile,
    }[tableName]);

    const tableColumnUtil = new TableColumnUtil<any, TTableTranslation>({
        ...mapAutoReportIdTableNameToColumnUtil[tableName],
        componentsFeature,
        fields: fields as Array<ITableField<EAutoReportIdTableAccountField>>,
        isTableResizeEnabled: false,
        translation: { ...translationCommon, ...tableFieldsTranslation },
        getColumnFixedType: () => false,
    });

    return (
        <Fragment>
            <PageTitle count={count} title={tableTitleTranslation.label}>
                {isFilterSelected() && renderDropFilterButton({
                    onClick: clearFilterHandler,
                    value: translationAutoReportId.table.title.dropFilters,
                })}
                {!isEditTable && renderSettingButton({
                    id: 1,
                    onClick: editTableHandler,
                    dataName: 'auto-report-edit-table',
                    value: tableTitleTranslation.edit,
                })}
                <DensityButton page={ECommonTranslationPages.autoReportId}/>
            </PageTitle>
            <Table
                basketAllIn={basketAllIn}
                columns={tableColumnUtil.customizeTableColumns()}
                counter={count}
                data={data}
                disableCheckedAll={!data.length}
                fields={fields}
                isDensity={isDensity}
                isLoading={isLoading}
                loadTableList={loadTableList}
                removedRowIndexes={removedRowIndexes}
                rowDataName={rowDataName}
                setTableSort={setTableSort}
                showCheckedColumn={isEditTable}
                sort={sort}
                switchCheckbox={switchCheckbox}
                tableCheckedItems={tableCheckedItems}
                tableDataName={tableName}
                tableName={tableName}
            />
            <AutoReportIdTableFooterStyled>
                <ButtonServiceUserDenied
                    disabled={isTableBtnDisabled}
                    kind="medium"
                    onClick={tableSaveHandler}
                >
                    {translationAutoReportId.table.footer.save}
                </ButtonServiceUserDenied>
                <LinkBlock
                    disabled={isTableBtnDisabled}
                    onClick={tableCancelHandler}
                >
                    {translationAutoReportId.table.footer.cancel}
                </LinkBlock>
            </AutoReportIdTableFooterStyled>
        </Fragment>
    );
};

interface IConnectedProps {
    isDensity: boolean;
    translationAutoReportId: IPageAutoReportIdTranslation;
    translationCommon: ICommonTranslation;
    translationPagePersonalAccount: IPagePersonalAccountTranslation;
    translationSubscriberMobile: IPageSubscriberMobileTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isDensity: makeSelectIsDensity(),
    translationAutoReportId: makeSelectPageAutoReportIdTranslation(),
    translationCommon: makeSelectTranslationCommon(),
    translationPagePersonalAccount: makeSelectTranslationPagePersonalAccount(),
    translationSubscriberMobile: makeSelectTranslationPageSubscriberMobile(),
});

interface IDispatchProps {
    restoreAllRows: (payload: ITableTableNamePayload) => void;
    saveTable: (payload: IAutoReportIdSaveTablePayload) => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    restoreAllRows: payload => dispatch(tableRowRestoreAllAction(payload)),
    saveTable: payload => dispatch(autoReportIdSaveTableAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AutoReportIdTable);
